/**
 * NativeShell — wires up Capacitor-only behaviours that improve the APK UX.
 *
 * 1. Android system Back button / edge-swipe gesture:
 *    - When the user is on a top-level **authenticated** route (`/staff`,
 *      `/admin`, `/gate`, `/visitor`), Back must NEVER unmount them out to
 *      `/login`. We intercept and either close any open modal, or show a
 *      "Tap Back again to exit" toast that exits the app on the 2nd tap.
 *    - When on `/login` or unknown routes with no SPA history, default
 *      "Tap again to exit" behaviour applies.
 *    - Sub-navigation inside a dashboard (e.g. opened a details modal)
 *      uses `window.history.back()` so modals close cleanly.
 *
 * 2. Browser popstate guard (web + Capacitor WebView):
 *    - If the URL is being changed to `/login` while a valid token is
 *      present in localStorage, we PREVENT the navigation and push the
 *      role-appropriate dashboard back on top of history. This stops the
 *      "Android swipe-back logged me out!" complaint.
 *
 * 3. App resume / pause logging — useful for diagnosing background kills.
 *
 * Renders nothing.
 */
import { useEffect, useRef } from 'react';

const TOP_AUTHED_ROUTES = new Set(['/staff', '/admin', '/gate', '/visitor']);

function isNative() {
  try {
    const cap = window.Capacitor;
    return !!(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform());
  } catch { return false; }
}

function hasValidToken() {
  try { return !!localStorage.getItem('token'); } catch { return false; }
}

function dashboardForUser() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    if (u.role === 'admin') return '/admin';
    if (u.role === 'gate') return '/gate';
    if (u.role === 'visitor') return '/visitor';
    return '/staff';
  } catch { return '/staff'; }
}

function showExitToast() {
  try {
    const el = document.createElement('div');
    el.textContent = 'Tap Back again to exit';
    el.style.cssText = 'position:fixed;left:50%;bottom:80px;transform:translateX(-50%);background:rgba(0,0,0,.85);color:#fff;padding:8px 16px;border-radius:9999px;font-size:13px;z-index:99999;pointer-events:none;font-family:-apple-system,system-ui,sans-serif';
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 1800);
  } catch {}
}

export default function NativeShell() {
  const lastBackTapRef = useRef(0);

  // ------- Popstate guard (runs in BOTH web and native) -------------------
  // Re-push the dashboard route if the WebView is about to navigate to
  // /login while a token still exists. This protects against the Android
  // edge-swipe-back gesture which fires popstate directly without going
  // through the Capacitor App backButton listener.
  useEffect(() => {
    const handler = () => {
      const path = window.location.pathname;
      if (path === '/login' && hasValidToken()) {
        const dest = dashboardForUser();
        try { window.history.pushState({}, '', dest); } catch {}
        // Trigger a soft re-render via a popstate the router listens to.
        try { window.dispatchEvent(new PopStateEvent('popstate')); } catch {}
      }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  // ------- Capacitor-only back button + lifecycle listeners ---------------
  useEffect(() => {
    if (!isNative()) return;
    let backHandle = null;
    let pauseHandle = null;
    let resumeHandle = null;

    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        backHandle = await App.addListener('backButton', async ({ canGoBack }) => {
          const path = window.location.pathname;
          const onTopAuthedRoute = TOP_AUTHED_ROUTES.has(path);

          // If a modal/dialog is open (has data-testid containing "modal"),
          // dismiss it by simulating Esc rather than navigating away.
          const openModal = document.querySelector('[data-testid$="-modal"], [role="dialog"][aria-hidden="false"]');
          if (openModal) {
            const closeBtn = openModal.querySelector('[data-testid$="-close"], [data-testid$="-close-btn"], [aria-label="Close"]');
            if (closeBtn) {
              try { closeBtn.click(); return; } catch {}
            }
          }

          // On a top-level authed route, do NOT navigate back (it would land
          // on /login and look like a logout). Use double-tap-to-exit.
          if (onTopAuthedRoute) {
            const now = Date.now();
            if (now - lastBackTapRef.current < 2000) {
              try { await App.exitApp(); } catch {}
              return;
            }
            lastBackTapRef.current = now;
            showExitToast();
            return;
          }

          // Otherwise (login, unknown), follow standard behaviour.
          if (canGoBack) {
            window.history.back();
            return;
          }
          const now = Date.now();
          if (now - lastBackTapRef.current < 2000) {
            try { await App.exitApp(); } catch {}
            return;
          }
          lastBackTapRef.current = now;
          showExitToast();
        });
        pauseHandle = await App.addListener('pause', () => {
          console.log('[carloo] app paused');
        });
        resumeHandle = await App.addListener('resume', () => {
          console.log('[carloo] app resumed');
        });
      } catch (e) {
        console.warn('[NativeShell] failed to attach listeners', e);
      }
    })();

    return () => {
      try { backHandle?.remove?.(); } catch {}
      try { pauseHandle?.remove?.(); } catch {}
      try { resumeHandle?.remove?.(); } catch {}
    };
  }, []);

  return null;
}
